import { useState, useEffect } from 'react';
import { Globe, Mail, Shield, Database, AlertTriangle, Cloud, FileJson, LayoutTemplate, Radio, RotateCcw } from 'lucide-react';
import Tabs, { type Tab } from '../components/common/Tabs';
import { systemService, tenantSettingsService } from '../services';
import type { GeneralSettings, SecuritySettings, NotificationSettings, BackupData, AlertSettings } from '../services';
import GeneralSettingsTab from './admin/system/GeneralSettingsTab';
import SecuritySettingsTab from './admin/system/SecuritySettingsTab';
import DiscoverySettingsTab from './admin/system/DiscoverySettingsTab';
import NotificationSettingsTab from './admin/system/NotificationSettingsTab';
import BackupRestoreTab from './admin/system/BackupRestoreTab';
import AlertsSettingsTab from './admin/system/AlertsSettingsTab';
import GlobalVSATab from './admin/system/GlobalVSATab';
import DefaultsTab from './admin/system/DefaultsTab';
import EmailTemplates from './admin/system/EmailTemplates';
import { useAuthStore } from '../stores/authStore';
import { useSettingsStore } from '../stores/settingsStore';
import { useTenantStore } from '../stores/tenantStore';
import { USER_TYPES } from '../utils/constants';
import { useToast } from '../components/common/ToastContainer';

/** Tabs visible to TenantAdmins */
const TENANT_ADMIN_TABS = ['general', 'security', 'notifications', 'templates', 'alerts'];

const SystemSettings = () => {
  const { user } = useAuthStore();
  const { getTenantId } = useTenantStore();
  const toast = useToast();
  const setSecurityInStore = useSettingsStore((s) => s.setSecurity);
  const isSuperAdmin = user?.type === USER_TYPES.SUPER_ADMIN;
  // A TenantAdmin edits their own tenant's settings. A ChannelAdmin has no
  // tenant of its own (tenantId: null) and edits the tenant picked in the
  // topbar — the same operating-tenant resolution the equipment pages use
  // (commit e57b54e). Without this a ChannelAdmin scoped to an empty tenant,
  // fetched nothing, and every tab hung on "Loading...".
  const operatingTenantId = user?.tenantId || getTenantId() || '';
  const [generalSettings, setGeneralSettings] = useState<GeneralSettings | null>(null);
  const [securitySettings, setSecuritySettingsLocal] = useState<SecuritySettings | null>(null);
  // VSNP-704 follow-up: every write to security settings must also push
  // into the global settings store so the idle-timeout hook picks up the
  // new idleTimeoutMinutes immediately (without requiring a tab reload).
  const setSecuritySettings = (s: SecuritySettings | null) => {
    setSecuritySettingsLocal(s);
    if (s) setSecurityInStore(s);
  };
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings | null>(null);
  const [backupData, setBackupData] = useState<BackupData | null>(null);
  const [alertSettings, setAlertSettings] = useState<AlertSettings | null>(null);
  const [discoverySettings, setDiscoverySettings] = useState<any | null>(null);
  const [appDiscoverySettings, setAppDiscoverySettings] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [tenantOverrides, setTenantOverrides] = useState<string[]>([]);

  useEffect(() => {
    const fetchAllSettings = async () => {
      try {
        setLoading(true);

        if (isSuperAdmin) {
          // SuperAdmin: fetch system-wide settings directly
          const [general, security, notifications, alerts, discovery, appDiscovery] = await Promise.all([
            systemService.getGeneral(),
            systemService.getSecurity(),
            systemService.getNotifications(),
            systemService.getAlerts(),
            systemService.getDiscovery(),
            systemService.getAppDiscovery(),
          ]);
          setGeneralSettings(general);
          setSecuritySettings(security);
          setNotificationSettings(notifications);
          setAlertSettings(alerts);
          setDiscoverySettings(discovery);
          setAppDiscoverySettings(appDiscovery);

          try {
            const backup = await systemService.getBackup();
            setBackupData(backup);
          } catch {
            // Backup endpoint may not exist yet
          }
        } else {
          // TenantAdmin / ChannelAdmin: fetch merged settings (tenant overrides + system defaults)
          const tenantId = operatingTenantId;
          if (tenantId) {
            const [systemGeneral, systemSecurity, systemNotifications, systemAlerts, discovery, tenantData] = await Promise.all([
              systemService.getGeneral(),
              systemService.getSecurity(),
              systemService.getNotifications(),
              systemService.getAlerts(),
              systemService.getDiscovery(),
              tenantSettingsService.getAll(tenantId).catch(() => null),
            ]);
            setDiscoverySettings(discovery);

            // Use tenant-merged settings if available, otherwise system defaults
            if (tenantData) {
              const allOverrides: string[] = [];
              if (tenantData.general?.overrides) allOverrides.push(...tenantData.general.overrides.map(k => `general.${k}`));
              if (tenantData.security?.overrides) allOverrides.push(...tenantData.security.overrides.map(k => `security.${k}`));
              if (tenantData.notifications?.overrides) allOverrides.push(...tenantData.notifications.overrides.map(k => `notifications.${k}`));
              if (tenantData.alerts?.overrides) allOverrides.push(...tenantData.alerts.overrides.map(k => `alerts.${k}`));
              setTenantOverrides(allOverrides);

              setGeneralSettings(tenantData.general?.settings as GeneralSettings || systemGeneral);
              setSecuritySettings(tenantData.security?.settings as SecuritySettings || systemSecurity);
              setNotificationSettings(tenantData.notifications?.settings as NotificationSettings || systemNotifications);
              setAlertSettings(tenantData.alerts?.settings as AlertSettings || systemAlerts);
            } else {
              setGeneralSettings(systemGeneral);
              setSecuritySettings(systemSecurity);
              setNotificationSettings(systemNotifications);
              setAlertSettings(systemAlerts);
            }
          }
        }
      } catch (err) {
        console.error('Failed to load system settings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllSettings();
  }, []);

  const handleResetToDefaults = async () => {
    if (isSuperAdmin) return; // SuperAdmin resets don't apply here
    const tenantId = operatingTenantId;
    if (!tenantId) return;

    try {
      await Promise.all(
        TENANT_ADMIN_TABS
          .filter(t => t !== 'templates') // email_templates uses separate table
          .map(tab => tenantSettingsService.resetCategory(tenantId, tab))
      );
      setTenantOverrides([]);
      toast.success('Settings Reset', 'All tenant overrides have been removed. System defaults are now active.');
      // Refetch
      window.location.reload();
    } catch (err: any) {
      toast.error('Reset Failed', err.message || 'Failed to reset settings');
    }
  };

  const allTabs: Tab[] = [
    {
      id: 'general',
      label: 'General',
      icon: Globe,
      content: (
        <GeneralSettingsTab
          key="general"
          settings={generalSettings}
          onUpdate={setGeneralSettings}
          isSuperAdmin={isSuperAdmin}
          tenantId={operatingTenantId}
        />
      )
    },
    {
      id: 'security',
      label: 'Security',
      icon: Shield,
      content: (
        <SecuritySettingsTab
          key="security"
          settings={securitySettings}
          onUpdate={setSecuritySettings}
          discoverySettings={discoverySettings}
          onDiscoveryUpdate={setDiscoverySettings}
          isSuperAdmin={isSuperAdmin}
          tenantId={operatingTenantId}
        />
      )
    },
    {
      id: 'discovery',
      label: 'Discovery',
      icon: Radio,
      content: (
        <DiscoverySettingsTab
          key="discovery"
          discoverySettings={discoverySettings}
          onDiscoveryUpdate={setDiscoverySettings}
          appDiscoverySettings={appDiscoverySettings}
          onAppDiscoveryUpdate={setAppDiscoverySettings}
        />
      )
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: Mail,
      content: <NotificationSettingsTab key="notifications" settings={notificationSettings} onUpdate={setNotificationSettings} isSuperAdmin={isSuperAdmin} tenantId={user?.tenantId || ''} />
    },
    {
      id: 'templates',
      label: 'Email Templates',
      icon: LayoutTemplate,
      content: <EmailTemplates />
    },
    // Backup & Restore tab — SuperAdmin only
    ...(isSuperAdmin ? [{
      id: 'backup',
      label: 'Backup & Restore',
      icon: Database,
      content: <BackupRestoreTab key="backup" data={backupData} onUpdate={setBackupData} />
    }] : []),
    {
      id: 'alerts',
      label: 'Alerts',
      icon: AlertTriangle,
      content: <AlertsSettingsTab key="alerts" settings={alertSettings} onUpdate={setAlertSettings} />
    },
    ...(isSuperAdmin ? [{
      id: 'global-vsa',
      label: 'Global VSA',
      icon: Cloud,
      content: <GlobalVSATab key="global-vsa" />
    }] : []),
    ...(isSuperAdmin ? [{
      id: 'defaults',
      label: 'Defaults',
      icon: FileJson,
      content: <DefaultsTab key="defaults" />
    }] : []),
  ];

  // Filter tabs based on user role
  const tabs = isSuperAdmin
    ? allTabs
    : allTabs.filter(tab => TENANT_ADMIN_TABS.includes(tab.id));

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading system settings...</div>;
  }

  // A ChannelAdmin operates on the topbar tenant selection; with "All my
  // tenants" chosen there is no concrete tenant to configure. Prompt for one
  // instead of rendering tabs that would sit on "Loading..." forever.
  if (!isSuperAdmin && !operatingTenantId) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        Select a tenant from the menu above to configure its settings.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isSuperAdmin ? 'System Settings' : 'Settings'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isSuperAdmin
              ? 'Configure system-wide settings and preferences'
              : 'Configure settings for your tenant'}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {!isSuperAdmin && tenantOverrides.length > 0 && (
            <button
              onClick={handleResetToDefaults}
              className="btn-secondary inline-flex items-center space-x-2"
            >
              <RotateCcw className="w-4 h-4" />
              <span>Reset to Defaults</span>
            </button>
          )}
        </div>
      </div>

      {/* Tenant override indicator */}
      {!isSuperAdmin && tenantOverrides.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          <p className="text-sm text-amber-800">
            <span className="font-medium">{tenantOverrides.length} setting{tenantOverrides.length !== 1 ? 's' : ''}</span> customized for your tenant.
            Settings not overridden use system defaults.
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="card">
        <Tabs tabs={tabs} />
      </div>
    </div>
  );
};

export default SystemSettings;
